export interface ArchiveCategory {
  id: string
  name: string
  detail: string
  products: string[]
  ctaLabel?: string
  image?: string
  imageAlt?: string
  imageFit?: 'contain' | 'cover'
}

export const ARCHIVE_CONTACT = {
  phone: '(941) 441-6131',
  phoneHref: 'tel:+19414416131',
  email: 'info@jagmedicalsolutions.com',
  address: '8499 S Tamiami Trail, #255, Sarasota, FL 34238',
}

export const ARCHIVE_SHOCKWAVE_IMAGE =
  'https://cdn.magicpatterns.com/patterns/generated-images/ab75f2f5-a243-4ac6-8994-cc4558777d81.jpg'

const CATEGORY_IMAGES = {
  laser:
    'https://assets.cdn.filesafe.space/wZmCOMOYUn4CfsoCHQM0/media/699f58ab8a039e9fc4bf5fb4.webp',
  shockwave:
    'https://assets.cdn.filesafe.space/wZmCOMOYUn4CfsoCHQM0/media/699cfefd5dfd506dd39bffa7.png',
  heit: 'https://assets.cdn.filesafe.space/wZmCOMOYUn4CfsoCHQM0/media/6982c2352dd9851edee6e2d3.webp',
  led: 'https://assets.cdn.filesafe.space/wZmCOMOYUn4CfsoCHQM0/media/699cff7d8ea4c98451074f30.png',
  thermal:
    'https://assets.cdn.filesafe.space/wZmCOMOYUn4CfsoCHQM0/media/6982c1c31dfc023c803236b0.webp',
}

export const archiveOrbitNodes: {
  id: string
  name: string
  detail: string
}[] = [
  {
    id: 'class-iv-laser',
    name: 'Class IV Laser',
    detail: 'Laser therapy systems for modern practices.',
  },
  {
    id: 'shockwave',
    name: 'Shockwave',
    detail: 'Focused and radial options to compare with JAG.',
  },
  {
    id: 'heit',
    name: 'High Energy Inductive Therapy',
    detail: 'A modality category to evaluate for your practice.',
  },
  {
    id: 'led',
    name: 'Whole Body Red Light / LED Therapy',
    detail: 'Whole-body and targeted light therapy options.',
  },
  {
    id: 'thermal',
    name: 'Thermal Imaging',
    detail: 'Thermal imaging options for assessment workflows.',
  },
  {
    id: 'peptides-stem-cells',
    name: 'Peptides/Stem Cells',
    detail:
      'Regenerative therapy options, including RegenOMedix, to evaluate with JAG.',
  },
  {
    id: 'used-equipment',
    name: 'Used Equipment',
    detail: 'Pre-owned and refurbished modality sourcing.',
  },
  {
    id: 'concierge',
    name: 'Concierge Modality Suite',
    detail: 'A curated system built around your doctor needs.',
  },
  {
    id: 'neuro-reformer',
    name: 'Neuro Reformer',
    detail:
      'Guided movement technology for rehab and functional recovery workflows.',
  },
]

export const archiveCategories: ArchiveCategory[] = [
  {
    id: 'class-iv-laser',
    name: 'Class IV Laser',
    detail:
      'Compare laser options with practical guidance on fit, team training, and implementation.',
    products: [
      'Endolaser HP',
      'Aspen Summit',
      'Aspen Ascent Compact',
      'MRM Alpha',
    ],
    image: CATEGORY_IMAGES.laser,
  },
  {
    id: 'shockwave',
    name: 'Shockwave',
    detail:
      'Understand focused and radial shockwave options before choosing the right direction for your office.',
    products: [
      'Focused Shockwave Endopuls FSWT',
      'Radial Shockwave Endopuls 811',
      'AWUSA Radial Shockwave',
      'AWUSA Dual-Radial Shockwave',
    ],
    image: CATEGORY_IMAGES.shockwave,
  },
  {
    id: 'heit',
    name: 'High Energy Inductive Therapy',
    detail:
      'A clear conversation about whether this modality belongs in your practice and how it would be implemented.',
    products: ['High Energy Inductive Therapy'],
    image: CATEGORY_IMAGES.heit,
  },
  {
    id: 'led',
    name: 'Whole Body Red Light / LED Therapy',
    detail:
      'Review LED therapy systems with a practical view of patient fit, staff workflow, and implementation.',
    products: [
      'LumaNova LumaPod',
      'LumaNova LumaBag',
      'TheraLight 360i',
      'TheraLight FIT',
      'TheraLight 360',
    ],
    image: CATEGORY_IMAGES.led,
  },
  {
    id: 'thermal',
    name: 'Thermal Imaging',
    detail:
      'Explore imaging options and how they can support your assessment workflow.',
    products: ['ThermoVision T2', 'WellVu Medical', 'WellVu Veterinary'],
    image: CATEGORY_IMAGES.thermal,
  },
  {
    id: 'peptides-stem-cells',
    name: 'Peptides/Stem Cells',
    detail:
      'Compare peptide and stem cell options with practical guidance on fit, workflow, and implementation in your practice.',
    products: ['Includes RegenOMedix'],
  },
  {
    id: 'used-equipment',
    name: 'Used Equipment',
    detail:
      'Talk with JAG about sourcing and evaluating pre-owned modalities as availability changes.',
    products: ['Pre-owned and refurbished modality availability'],
    image:
      'https://asset.imagine.art/video/thumbnails/ce37a282-5aa7-44c8-b595-bad579344376',
    imageAlt: 'Used medical modality equipment in a clean clinic',
    imageFit: 'cover',
  },
  {
    id: 'concierge',
    name: 'Concierge Modality Suite',
    detail:
      'A coordinated approach to selecting modalities that work together around your practice goals.',
    products: ['Curated modality system'],
    image:
      'https://asset.imagine.art/video/thumbnails/fe654d8a-8236-4491-a674-62fed502fdf4',
    imageAlt: 'Concierge modality planning and implementation support',
    imageFit: 'cover',
  },
  {
    id: 'neuro-reformer',
    name: 'Neuro Reformer',
    detail:
      'Neuromuscular re-education and guided movement technology for practices evaluating rehab, coordination, balance, and functional recovery workflows.',
    products: [
      'Compare fit, training needs, patient use cases, and implementation requirements before choosing a system.',
    ],
    ctaLabel: 'Call About This Category',
    image:
      'https://asset.imagine.art/video/thumbnails/193c7ae9-9406-44b8-8ca5-11c1498093a1',
    imageAlt: 'Neuro Reformer neuromuscular rehabilitation setup',
    imageFit: 'cover',
  },
]

export const archiveConciergeModalities = [
  {
    name: 'Class IV Laser',
    desc: 'Compare power, footprint, training, and patient-use fit before choosing a system.',
  },
  {
    name: 'Shockwave',
    desc: 'Review focused and radial options with protocol and workflow context.',
  },
  {
    name: 'High Energy Inductive Therapy',
    desc: 'Evaluate whether the modality fits your treatment mix and space.',
  },
  {
    name: 'LED Light Therapy',
    desc: 'Plan around patient flow, placement, and staff use before purchase.',
  },
  {
    name: 'Thermal Imaging',
    desc: 'Understand where imaging supports assessment and communication.',
  },
  {
    name: 'Peptides/Stem Cells',
    desc: 'Compare regenerative therapy options, including RegenOMedix, with practical implementation context.',
  },
]

export const archivePartnerBrands: {
  name: string
  src?: string
  wordmark?: 'aspen' | 'multi-radiance' | 'lumanova' | 'brand'
}[] = [
  { name: 'Aspen Laser', wordmark: 'aspen' },
  { name: 'Multi Radiance Medical', wordmark: 'multi-radiance' },
  { name: 'LumaNova', wordmark: 'lumanova' },
  {
    name: 'TheraLight',
    src: 'https://cdn.magicpatterns.com/uploads/vttP1Sb7gikoGaFWY35ZNt/theralight-logo-tight.png',
  },
  {
    name: 'WellVu',
    src: 'https://cdn.magicpatterns.com/uploads/qMimGwm5vM6HRxKcW7oosi/wellvu-logo.png',
  },
  {
    name: 'RegenOMedix',
    src: 'https://cdn.magicpatterns.com/uploads/kjFX5H2HTkPc53njVnmw7m/regenomedix-logo.png',
  },
  {
    name: 'PainWave',
    src: 'https://cdn.magicpatterns.com/uploads/tEGMAXAr51egcgWdJp6WFL/painwave-logo.png',
  },
  {
    name: 'CTN-USA',
    src: 'https://cdn.magicpatterns.com/uploads/1hhfWhvefeDvCfWaqe9ZMJ/ctn-usa-logo.png',
  },
  {
    name: 'Invisa-RED',
    src: 'https://cdn.magicpatterns.com/uploads/iGeardWcM8A5hHzBqR2jS6/invisa-red-logo.png',
  },
  {
    name: 'HydroWave',
    src: 'https://cdn.magicpatterns.com/uploads/vwTK5cExYRc6dcQX67vs1W/hydrowave-logo.png',
  },
  {
    name: 'Summus',
    src: 'https://cdn.magicpatterns.com/uploads/pd7fH26BN3F6wMJQurtyqN/summus-logo.png',
  },
  {
    name: 'NeuroReformer',
    src: 'https://cdn.magicpatterns.com/uploads/cr2fRFUb942Q6xKwFVyH4e/neuroreformer-logo.png',
  },
  {
    name: 'Team Medical Group',
    src: 'https://cdn.magicpatterns.com/uploads/9nXJ6g9P32g5r6G7ZpCTk8/team-medical-group-logo.png',
  },
  { name: 'Visnova', wordmark: 'brand' },
]
